import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid, Thermometer, SprayCan, Truck, Boxes, ClipboardCheck, AlertTriangle,
  Wrench, FileText, LifeBuoy, ChefHat, QrCode, Tag, Package, Camera, GraduationCap, ScrollText,
  PackageOpen, Zap, FileWarning, Menu as MenuIcon, X, Bell, Phone, LogOut, Lock, ShieldCheck, MessageCircle, ChevronRight, CheckCheck, CalendarDays, Factory, Snowflake, ListChecks, Smartphone, Hourglass, Stethoscope, PenLine, Droplets, FlaskConical, PackageX, BookOpen,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api, { BRAND, formatApiErrorDetail } from "../../lib/api";
import { syncDeviceCopy } from "../../lib/safeguardCache";
import { warmOfflineCache } from "../../lib/offlineSync";
import OfflineStatusBar from "../../components/OfflineStatusBar";
import { RecallBanner } from "../../components/RecallBanner";
import { formatItalianDateTime } from "../../lib/dateUtils";
import { romeDateKey } from "../../lib/dayRollover";
import { Logo } from "../../components/Logo";
import HistoryNavButtons from "../../components/HistoryNavButtons";
import { AcademyProvider } from "../../academy/AcademyContext";
import AcademyOverlay from "../../academy/AcademyOverlay";
import { StatusBadge, Spinner, SoundToggle } from "../../components/ui-kit";
import { toast } from "sonner";

const ICONS = {
  panoramica: LayoutGrid, "accessi-rapidi": Zap, "ddt-nuovo": Truck, "ddt-emessi": Truck, "lotti-senza-documento": FileWarning, "check-up": Stethoscope, "controlli-oggi": ClipboardCheck, temperature: Thermometer, "altri-controlli": ClipboardCheck,
  pulizie: SprayCan, ricevimento: Truck, cottura: ChefHat, tracciabilita: Boxes,
  ingredienti: Boxes, lotti: Package, "carica-lotti": PenLine, ricette: ChefHat, etichette: Tag, qr: QrCode,
  "non-conformita": AlertTriangle, attrezzature: Wrench, "mezzi-trasporto": Truck, "registro-automezzi": Truck, fornitori: Truck,
  magazzino: Package, "smart-lotto": Camera, personale: GraduationCap, documenti: FileText, registri: ScrollText,
  assistenza: LifeBuoy, "programmazione-produzioni": CalendarDays, "crea-produzione": Factory,
  abbattimento: Snowflake,
  "olio-frittura": Droplets,
  ritiri: PackageX,
  "prodotti-abbattuti": ListChecks,
  "shelf-life": Hourglass,
  moca: PackageOpen,
  sanificanti: FlaskConical,
};

function WhatsAppBrandIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.075-.792.372-.272.297-1.04 1.016-1.04 2.479s1.065 2.875 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.99c-.002 5.45-4.437 9.884-9.888 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.14 1.588 5.945L.057 24l6.3-1.654a11.882 11.882 0 0 0 5.689 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

// Menu completo (i moduli non attivi in Fase 1 mostrano il lucchetto)
const MENU = [
  // "Accessi rapidi" porta la Home direttamente alle schede colorate delle operazioni frequenti.
  { group: "PANORAMICA", items: [
    { to: "/app", key: "panoramica", label: "Panoramica", end: true, mod: null },
    { to: "/app#accessi-rapidi", hash: "#accessi-rapidi", key: "accessi-rapidi", label: "Accessi rapidi", end: true, mod: null },
  ] },
  // Diagnosi: "Controlli di oggi" apre direttamente la giornata di oggi con
  // il pulsante unico che registra come conformi tutti i controlli attestabili.
  { group: "DIAGNOSI", items: [
    { to: "/app/check-up", key: "check-up", label: "Check-up", mod: null },
    { to: "/app/controlli?giorno=oggi", key: "controlli-oggi", label: "Controlli di oggi", mod: null },
  ]},
  { group: "ARRIVO MERCI", items: [
    { to: "/app/smart-lotto", key: "smart-lotto", label: "Carica lotti foto smart", mod: "smart_lotto" },
    { to: "/app/carica-lotti", key: "carica-lotti", label: "Carica lotti manuale", mod: "materie_prime" },
    { to: "/app/lotti", key: "lotti", label: "Lotti registrati (lista)", mod: "materie_prime" },
  ]},
  { group: "CONTROLLI HACCP", items: [
    { to: "/app/temperature", key: "temperature", label: "Temperature", mod: "temperature" },
    { to: "/app/olio-frittura", key: "olio-frittura", label: "Olio di frittura", mod: "temperature" },
    { to: "/app/pulizie", key: "pulizie", label: "Pulizie e sanificazioni", mod: "pulizie" },
    { to: "/app/altri-controlli", key: "altri-controlli", label: "Altri controlli HACCP" },
    { to: "/app/registro-automezzi", key: "registro-automezzi", label: "Registro automezzi", mod: "anagrafiche" },
  ]},
  { group: "PRODUZIONE", items: [
    { to: "/app/crea-produzione", key: "crea-produzione", label: "Crea produzione", mod: "ricette" },
    { to: "/app/produzioni", key: "produzioni", label: "Produzioni create (lista)", mod: "ricette" },
    { to: "/app/programmazione-produzioni", key: "programmazione-produzioni", label: "Programmazione produzioni", mod: "programmazione_produzioni" },
    { to: "/app/abbattimento", key: "abbattimento", label: "Abbattimento prodotti", mod: "temperature" },
    { to: "/app/prodotti-abbattuti", key: "prodotti-abbattuti", label: "Prodotti abbattuti (lista)", mod: "temperature" },
  ]},
  { group: "CONFIGURAZIONE", items: [
    { to: "/app/attrezzature", key: "attrezzature", label: "Attrezzature", mod: "anagrafiche" },
    { to: "/app/mezzi-trasporto", key: "mezzi-trasporto", label: "Mezzi di trasporto", mod: "anagrafiche" },
    { to: "/app/fornitori", key: "fornitori", label: "Fornitori", mod: "fornitori" },
    { to: "/app/ingredienti", key: "ingredienti", label: "Materie prime", mod: "materie_prime" },
    // Modulo aggiuntivo: chi non usa imballaggi (solo vetrina) non deve nemmeno vederlo.
    { to: "/app/moca", key: "moca", label: "MOCA – imballaggi", mod: "moca", hideWhenLocked: true },
    { to: "/app/sanificanti", key: "sanificanti", label: "Prodotti sanificanti", mod: "pulizie" },
    { to: "/app/ricette", key: "ricette", label: "Ricette", mod: "ricette" },
  ]},
  { group: "GESTIONE", items: [
    { to: "/app/non-conformita", key: "non-conformita", label: "Non conformità", mod: "non_conformita" },
    // Obbligo per tutti (Reg. CE 178/2002 art. 19): sempre attivo, nessun modulo.
    { to: "/app/ritiri", key: "ritiri", label: "Ritiro e richiamo prodotti", mod: null },
    { to: "/app/magazzino", key: "magazzino", label: "Magazzino", mod: "magazzino" },
    { to: "/app/ddt/nuovo", key: "ddt-nuovo", label: "Crea DDT spedizione", mod: "ddt" },
  ]},
  { group: "RICERCA", items: [
    { to: "/app/tracciabilita", key: "tracciabilita", label: "Ricerca lotto e DDT", mod: "materie_prime" },
    { to: "/app/ddt", key: "ddt-emessi", label: "DDT di spedizione emessi", end: true, mod: "ddt" },
    { to: "/app/tracciabilita?mancanti=1", query: "?mancanti=1", key: "lotti-senza-documento", label: "Lotti senza DDT o fattura", mod: "materie_prime" },
  ]},
  { group: "STAMPA", items: [
    { to: "/app/etichette-abbattuti", key: "abbattimento", label: "Etichette prodotti abbattuti", mod: "temperature" },
    { to: "/app/etichette", key: "etichette", label: "Etichette & QR code", mod: "etichette" },
    { to: "/app/stampa-qr", key: "stampa-qr", label: "Stampa QR code", mod: "report_base" },
    { to: "/app/report", key: "documenti", label: "Report e verbali", mod: "report_base" },
    { to: "/app/report", key: "registri", label: "Report mensili", mod: "report_mensili" },
    { to: "/app/libro-ingredienti", key: "libro-ingredienti", label: "Libro ingredienti", mod: "report_base" },
  ]},
  { group: "ISPEZIONE ASL/NAS", items: [
    { to: "/app/ispezione", key: "ispezione", label: "Ispezione ASL/NAS", mod: null, hideForRoles: ["addetto"] },
  ]},
  { group: "AZIENDA", items: [
    { to: "/app/dati-azienda", key: "dati-azienda", label: "Dati azienda", mod: null },
    { to: "/app/cambia-password", key: "cambia-password", label: "Cambia password", mod: null, hideForRoles: ["addetto"] },
  ] },
  { group: "DF FOOD SAFETY EXPERT ENGINE", items: [
    { to: "/app/shelf-life", key: "shelf-life", label: "Shelf life", mod: "shelf_life_expert" },
  ]},
  { group: "ASSISTENZA", items: [{ to: "/app/assistenza", key: "assistenza", label: "Contatta Dottor D", mod: null }] },
];

// Voci che puntano alla stessa pagina con un #ancora o un ?filtro: è attiva solo
// quella che corrisponde, non anche la voce "semplice" della stessa pagina.
const VARIANT_ITEMS = MENU.flatMap((sec) => sec.items).filter((it) => it.hash || it.query);
const itemMatches = (it, location) => {
  if (it.hash) return location.hash === it.hash;
  if (it.query) return location.search === it.query;
  const path = it.to.split(/[?#]/)[0];
  return !VARIANT_ITEMS.some((v) => v.to.split(/[?#]/)[0] === path && (v.hash ? location.hash === v.hash : location.search === v.query));
};

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [ctx, setCtx] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeDay, setActiveDay] = useState(romeDateKey);
  const [draftRecipeCount, setDraftRecipeCount] = useState(0);
  const isManager = ["titolare", "super_admin"].includes(user?.role);
  const navBlocked = isManager && draftRecipeCount > 0;

  useEffect(() => { api.get("/app/context").then(({ data }) => setCtx(data)).catch(() => {}); }, []);
  // SafeGuard: copia in background degli ultimi report verificati, per Ispezione ASL/NAS senza server.
  useEffect(() => { if (user) syncDeviceCopy(user).catch(() => {}); }, [user]);
  // SafeGuard: prepara le schermate dei controlli di oggi per l'uso senza connessione.
  useEffect(() => { if (ctx) warmOfflineCache(ctx).catch(() => {}); }, [ctx]);

  // Ripristina l'icona "Aggiungi alla schermata Home" alla versione cliente
  // (navy): AdminLayout la sostituisce con la variante oro per l'area admin,
  // così le due aree restano distinguibili a colpo d'occhio sul telefono.
  useEffect(() => {
    const iconLink = document.querySelector('link[rel="apple-touch-icon"]');
    if (iconLink) iconLink.href = "/apple-touch-icon.png";
  }, []);

  const refreshDraftCount = useMemo(() => {
    if (!isManager) return () => {};
    return () => api.get("/app/catalog/recipes", { params: { status: "bozza" } })
      .then(({ data }) => setDraftRecipeCount((data.recipes || []).length))
      .catch(() => {});
  }, [isManager]);
  useEffect(() => {
    if (!isManager) { setDraftRecipeCount(0); return undefined; }
    refreshDraftCount();
    const t = window.setInterval(refreshDraftCount, 60000);
    return () => window.clearInterval(t);
  }, [isManager, refreshDraftCount]);
  useEffect(() => {
    // Il report serale dei registri (sola lettura, aperto dalla notifica) resta consultabile anche col blocco ricette in bozza.
    if (navBlocked && location.pathname !== "/app/ricette" && !location.pathname.startsWith("/app/report-giornaliero/")) nav("/app/ricette", { replace: true });
  }, [navBlocked, location.pathname, nav]);
  const guardNav = (to) => (e) => {
    if (navBlocked && to !== "/app/ricette") {
      e.preventDefault();
      toast.error("Approva prima le ricette in bozza per continuare");
      return;
    }
    setOpen(false);
  };
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => { if (event.key === "Escape") setOpen(false); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);
  useEffect(() => {
    const synchronizeDay = () => {
      const currentDay = romeDateKey();
      setActiveDay((previousDay) => {
        if (previousDay === currentDay) return previousDay;
        api.get("/app/context").then(({ data }) => setCtx(data)).catch(() => {});
        return currentDay;
      });
    };
    // Moduli attivati/disattivati dal consulente mentre l'app era già aperta:
    // al ritorno sull'app si rilegge il contesto (al massimo ogni 30 secondi),
    // così i lucchetti si aggiornano senza dover chiudere e riaprire.
    let lastContextFetch = Date.now();
    const refreshContext = () => {
      if (Date.now() - lastContextFetch < 30000) return;
      lastContextFetch = Date.now();
      api.get("/app/context").then(({ data }) => setCtx(data)).catch(() => {});
    };
    const onFocus = () => { synchronizeDay(); refreshContext(); };
    const timer = window.setInterval(synchronizeDay, 15000);
    const onVisibility = () => { if (!document.hidden) onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const modMap = useMemo(() => {
    const m = {};
    (ctx?.modules || []).forEach((x) => { m[x.key] = x; });
    return m;
  }, [ctx]);

  const isLocked = (modKey) => {
    if (!modKey) return false;
    const m = modMap[modKey];
    if (!m) return true;
    return !(m.active && m.available_phase1);
  };

  if (!ctx) return <div className="min-h-screen grid place-items-center bg-haccp-bg"><Spinner /></div>;

  const tenant = ctx.tenant || {};
  const suspended = tenant.status && tenant.status !== "active";

  // Bloccata (scaduti i 3 giorni di limitazione senza regolarizzazione):
  // schermata unica, non il layout normale con un banner sopra contenuti
  // che tanto fallirebbero comunque (l'API blocca quasi tutto tranne
  // /app/context, vedi _enforce_contract_and_modules in security.py).
  if (tenant.status === "blocked") {
    return (
      <div className="grid min-h-screen min-h-[100dvh] place-items-center bg-navy px-4 text-center" data-testid="blocked-screen">
        <div className="max-w-md">
          <ShieldCheck className="mx-auto mb-4 h-14 w-14 text-red-400" />
          <h1 className="font-heading text-2xl font-bold uppercase tracking-wide text-white sm:text-3xl">Piattaforma bloccata</h1>
          <p className="mt-3 font-semibold text-red-300">Mancato rispetto del contratto.</p>
          <p className="mt-2 text-white/80">Contattare il Dott. Dario Feliciano per la riattivazione. I dati restano conservati.</p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <a href={`tel:${BRAND.phone}`} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 font-semibold text-navy"><Phone className="w-4 h-4" /> Chiama</a>
            <a href={`https://wa.me/${BRAND.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 font-semibold text-white"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} className="mt-6 text-sm text-white/50 underline" data-testid="blocked-logout">Esci</button>
        </div>
      </div>
    );
  }

  const Sidebar = (
    <aside className="flex h-full w-[min(18rem,calc(100vw-3.5rem))] shrink-0 flex-col overflow-hidden bg-navy text-white lg:w-72">
      <div className="border-b border-white/10 p-5 pt-[max(1.25rem,env(safe-area-inset-top))]">
        <NavLink
          to="/app"
          end
          onClick={guardNav("/app")}
          aria-label="Torna alla schermata iniziale"
          data-testid="sidebar-home-logo"
          className="inline-flex rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
        >
          <Logo variant="dark" size="sm" />
        </NavLink>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" data-testid="client-sidebar">
        {/* Guida illustrata: pagina statica in public/guida, fuori da React Router; sempre attiva */}
        <a href="/guida/" target="_blank" rel="noopener" onClick={() => setOpen(false)} data-testid="nav-guida-online"
          className="flex items-center gap-3 rounded-lg border border-gold-bright/30 bg-gold-bright/10 px-3 py-2.5 min-h-[44px] text-sm font-body font-medium text-white transition-colors hover:bg-gold-bright/20">
          <BookOpen className="h-[18px] w-[18px] shrink-0 text-gold-bright" strokeWidth={1.6} />
          <span className="flex-1 truncate">Guida on line</span>
        </a>
        {navBlocked && (
          <NavLink to="/app/ricette" onClick={() => setOpen(false)} data-testid="sidebar-draft-recipes"
            className="flex items-start gap-2 rounded-lg border border-red-300/40 bg-red-500/15 px-3 py-2.5 text-xs leading-snug text-red-100 animate-pulse motion-reduce:animate-none">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-300" />
            <span><strong className="font-semibold">Menu bloccato.</strong> Approva le ricette in bozza per sbloccare i lucchetti.</span>
          </NavLink>
        )}
        {MENU.map((sec) => ({ ...sec, items: sec.items.filter((it) => !it.hideForRoles?.includes(user?.role) && !(it.hideWhenLocked && isLocked(it.mod))) }))
          .filter((sec) => sec.items.length).map((sec) => (
          <div key={sec.group}>
            <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.18em] text-white/40 font-body">{sec.group}</div>
            <div className="space-y-0.5">
              {sec.items.map((it) => {
                const locked = isLocked(it.mod);
                const navLocked = navBlocked && it.to !== "/app/ricette";
                const Icon = ICONS[it.key] || ClipboardCheck;
                return (
                  <NavLink key={it.to} to={it.to} end={it.end} onClick={guardNav(it.to)}
                    data-testid={`nav-${it.key}`}
                    aria-disabled={navLocked}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-body min-h-[44px] transition-colors ${
                        navLocked ? "text-white/30 cursor-not-allowed hover:bg-transparent hover:text-white/30" :
                        isActive && !locked && itemMatches(it, location) ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                      }`}>
                    <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} />
                    <span className="flex-1 truncate">{it.label}</span>
                    {navLocked && <Lock className="w-3.5 h-3.5 text-red-300/70" />}
                    {!navLocked && locked && <Lock className="w-3.5 h-3.5 text-gold-bright/70" />}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3 pb-[max(.75rem,env(safe-area-inset-bottom))]">
        <SoundToggle showLabel testid="client-sound-toggle"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white min-h-[44px]" />
        <button onClick={() => { logout(); nav("/login"); }} data-testid="client-logout"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white min-h-[44px]">
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.6} /> Esci
        </button>
      </div>
    </aside>
  );

  return (
    <AcademyProvider>
    <div className="app-shell flex min-h-screen min-h-[100dvh] w-full max-w-full overflow-x-clip bg-haccp-bg">
      {/* Sidebar desktop */}
      <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>

      {/* Drawer mobile */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu principale">
          <button className="absolute inset-0 h-full w-full bg-black/50" onClick={() => setOpen(false)} aria-label="Chiudi menu" />
          <div className="absolute inset-y-0 left-0 max-w-full shadow-2xl">{Sidebar}</div>
          <button className="absolute right-2 top-[max(.5rem,env(safe-area-inset-top))] grid h-11 w-11 place-items-center rounded-full bg-navy/90 text-white" onClick={() => setOpen(false)} aria-label="Chiudi menu"><X /></button>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-30 border-b border-hairline bg-white/95 pt-[env(safe-area-inset-top)] backdrop-blur">
          <div className="grid h-16 min-w-0 grid-cols-[auto_1fr_auto] items-center gap-2 px-3 sm:gap-3 sm:px-6">
            <div className="flex min-w-0 shrink-0 items-center gap-1 sm:gap-1.5">
              {/* Telefono e tablet: il simbolo DF HACCP in alto a sinistra riporta sempre alla pagina iniziale. */}
              <NavLink to="/app" end onClick={guardNav("/app")} aria-label="DF HACCP — torna alla pagina iniziale" title="Pagina iniziale"
                data-testid="mobile-home-logo"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-navy shadow-sm ring-1 ring-navy/10 transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 lg:hidden">
                <img src={BRAND.symbol} alt="DF HACCP" className="h-8 w-8 object-contain" />
              </NavLink>
              <button className="grid h-11 w-9 sm:w-11 shrink-0 place-items-center rounded-lg text-navy lg:hidden" onClick={() => setOpen(true)} aria-label="Apri menu" aria-expanded={open} data-testid="mobile-menu-toggle"><MenuIcon /></button>
              <HistoryNavButtons />
            </div>
            <NavLink
              to="/app"
              end
              onClick={guardNav("/app")}
              aria-label="Torna alla schermata iniziale"
              data-testid="header-home-logo"
              className="flex w-full min-w-0 items-center justify-center gap-2 justify-self-center overflow-hidden rounded-lg text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 lg:w-auto"
            >
              {tenant.logo_url && <img src={tenant.logo_url} alt="" className="h-8 w-auto shrink-0 object-contain" />}
              <div className="min-w-0 max-w-full sm:max-w-[260px] lg:max-w-none">
                <div className="font-heading text-sm sm:text-base font-semibold text-navy truncate leading-tight">{tenant.name}</div>
                <div className="hidden min-[360px]:block text-[10px] sm:text-[11px] text-slate-400 font-body truncate">{ctx.role_label} · {tenant.plan_label}</div>
              </div>
            </NavLink>
            <div className="flex min-w-0 shrink-0 items-center justify-self-end gap-1 sm:gap-2">
              <div className="hidden sm:block"><StatusBadge status={suspended ? "rosso" : "verde"} testid="area-status">
                {suspended ? (tenant.status === "limited" ? "Area limitata" : "Area bloccata") : "Area attiva"}
              </StatusBadge></div>
              <a
                href={`https://wa.me/${BRAND.whatsapp.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-[#25D366] bg-white text-[#20b858] shadow-sm transition-colors hover:bg-green-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20b858] focus-visible:ring-offset-2 md:inline-flex"
                aria-label="Contatta l'assistenza tramite WhatsApp"
                title="WhatsApp"
                data-testid="header-whatsapp"
              >
                <WhatsAppBrandIcon />
              </a>
              <a
                href={`tel:${BRAND.phone.replace(/[^\d+]/g, "")}`}
                className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-red-600 bg-white text-red-600 shadow-sm transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 md:inline-flex"
                aria-label="Chiama telefonicamente l'assistenza"
                title="Chiama assistenza"
                data-testid="header-telefono"
              >
                <Phone className="h-5 w-5" strokeWidth={2.1} />
              </a>
              <Notifications />
              <div className="hidden h-10 w-10 shrink-0 place-items-center rounded-full bg-navy font-heading text-sm text-white min-[390px]:grid" data-testid="header-avatar">
                {(user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
            </div>
          </div>
        </header>

        <PushSetup user={user} />

        {suspended && <SuspensionBanner tenant={tenant} />}
        {user?.impersonated_by && <ImpersonationBanner tenantName={tenant.name} />}
        {["titolare", "responsabile"].includes(user?.role) && <ComplianceAlertBanner impersonated={Boolean(user?.impersonated_by)} onOpen={() => nav("/app/controlli")} />}
        <RecallBanner />
        <OfflineStatusBar />
        {navBlocked && <DraftRecipesBanner count={draftRecipeCount} onOpen={() => nav("/app/ricette")} />}

        <main className="app-content mx-auto w-full max-w-[1400px] min-w-0 flex-1 overflow-x-clip p-3 pb-24 sm:p-6 sm:pb-24 lg:p-8 lg:pb-8">
          <Outlet key={activeDay} context={{ ctx, modMap, isLocked, refresh: () => api.get("/app/context").then(({ data }) => setCtx(data)), refreshDraftCount }} />
        </main>

        <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-hairline bg-white/95 px-2 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_rgba(10,31,68,.08)] backdrop-blur lg:hidden" aria-label="Navigazione rapida">
          <MobileNavLink to="/app" end icon={LayoutGrid} label="Home" onClick={guardNav("/app")} />
          <MobileNavLink to="/app/controlli" icon={ClipboardCheck} label="Controlli" onClick={guardNav("/app/controlli")} />
          <MobileNavLink to="/app/check-up" icon={Stethoscope} label="Check-up" onClick={guardNav("/app/check-up")} />
          <button type="button" onClick={() => setOpen(true)} className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-lg px-1 text-[11px] font-medium text-slate-500" aria-label="Apri tutte le sezioni"><MenuIcon className="h-5 w-5" /><span>Menu</span></button>
        </nav>
      </div>
      <AcademyOverlay />
    </div>
    </AcademyProvider>
  );
}

function pushKeyToBytes(value) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const binary = window.atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function PushSetup({ user }) {
  const [state, setState] = useState("checking");
  const [testing, setTesting] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("df_push_setup_collapsed") === "1"; } catch { return false; }
  });
  const isTitolare = user?.role === "titolare";
  useEffect(() => {
    if (!isTitolare) return;
    const check = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) { setState("unsupported"); return; }
      const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
      const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
      if (isIos && !standalone) { setState("install-ios"); return; }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setState(subscription ? "enabled" : Notification.permission === "denied" ? "denied" : "available");
    };
    check().catch(() => setState("unsupported"));
  }, [isTitolare]);
  if (!isTitolare || state === "checking" || state === "unsupported") return null;
  const enable = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { setState("denied"); return; }
      const [{ data }, registration] = await Promise.all([api.get("/app/push/public-key"), navigator.serviceWorker.ready]);
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: pushKeyToBytes(data.public_key) });
      await api.post("/app/push/subscribe", subscription.toJSON());
      setState("enabled");
      toast.success("Notifiche push attivate su questo dispositivo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Non è stato possibile attivare le notifiche push");
    }
  };
  const disable = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await api.post("/app/push/unsubscribe", { endpoint: subscription.endpoint });
      await subscription.unsubscribe();
    }
    setState("available");
    toast.success("Notifiche push disattivate su questo dispositivo");
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/app/push/test");
      toast.success("Notifica di prova inviata al tuo cellulare");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invio della notifica di prova non riuscito");
    } finally {
      setTesting(false);
    }
  };
  const collapse = () => {
    setCollapsed(true);
    try { localStorage.setItem("df_push_setup_collapsed", "1"); } catch { /* ignore */ }
  };
  const expand = () => {
    setCollapsed(false);
    try { localStorage.removeItem("df_push_setup_collapsed"); } catch { /* ignore */ }
  };
  if (collapsed) {
    return <div className={`border-b px-4 py-1.5 sm:px-6 ${state === "enabled" ? "border-emerald-200 bg-emerald-50" : "border-blue-200 bg-blue-50"}`} data-testid="push-setup-collapsed">
      <div className="mx-auto flex max-w-[1400px] items-center">
        <button
          type="button"
          onClick={expand}
          aria-label="Mostra lo stato delle notifiche sul cellulare"
          title="Notifiche sul cellulare"
          className={`flex h-8 w-8 items-center justify-center rounded-full ${state === "enabled" ? "text-emerald-700 hover:bg-emerald-100" : "text-blue-700 hover:bg-blue-100"}`}
        >
          <Smartphone className="h-4 w-4" />
        </button>
      </div>
    </div>;
  }
  return <div className={`border-b px-4 py-3 sm:px-6 ${state === "enabled" ? "border-emerald-200 bg-emerald-50" : "border-blue-200 bg-blue-50"}`} data-testid="push-setup">
    <div className="mx-auto flex max-w-[1400px] flex-col gap-3 text-sm sm:flex-row sm:items-center">
      <Bell className={`h-5 w-5 shrink-0 ${state === "enabled" ? "text-emerald-600" : "text-blue-700"}`} />
      <div className="flex-1 text-navy">{state === "enabled" ? <><strong>Notifiche sul cellulare attive.</strong> Riceverai i promemoria dei registri mancanti.</> : state === "install-ios" ? <><strong>Attiva le notifiche su iPhone.</strong> Da Safari premi Condividi, “Aggiungi alla schermata Home”, poi apri DF HACCP dalla nuova icona.</> : state === "denied" ? <><strong>Notifiche bloccate.</strong> Riattivale dalle impostazioni del browser o del telefono.</> : <><strong>Ricevi i promemoria sul cellulare.</strong> Attiva gratuitamente le notifiche per i registri mancanti.</>}</div>
      {state === "available" && <button type="button" onClick={enable} className="min-h-[42px] rounded-lg bg-navy px-4 font-semibold text-white hover:bg-navy/90">Attiva notifiche</button>}
      {state === "enabled" && <div className="flex flex-col gap-2 sm:flex-row"><button type="button" disabled={testing} onClick={sendTest} className="min-h-[40px] rounded-lg bg-emerald-700 px-3 font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">{testing ? "Invio…" : "Invia notifica di prova"}</button><button type="button" disabled={testing} onClick={disable} className="min-h-[40px] rounded-lg border border-emerald-400 bg-white px-3 font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60">Disattiva</button></div>}
      <button type="button" onClick={collapse} aria-label="Minimizza questo avviso" title="Minimizza" className="shrink-0 self-start rounded-full p-1.5 text-navy/50 hover:bg-black/5 hover:text-navy sm:self-center">
        <X className="h-4 w-4" />
      </button>
    </div>
  </div>;
}

function MobileNavLink({ to, end, icon: Icon, label, onClick }) {
  return <NavLink to={to} end={end} onClick={onClick} className={({ isActive }) => `flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-lg px-1 text-[11px] font-medium ${isActive ? "text-navy" : "text-slate-500"}`}><Icon className="h-5 w-5" /><span>{label}</span></NavLink>;
}

function Notifications() {
  const [items, setItems] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [open, setOpen] = useState(false);
  const activeDay = useRef(romeDateKey());
  const nav = useNavigate();
  const fetchN = () => Promise.all([
    api.get("/app/notifications"),
    api.get("/app/catalog/expiring-lots?days=7"),
    api.get("/app/action-items"),
  ]).then(([notifications, expiring, actions]) => {
    setItems(notifications.data.items || []);
    const lotItems = (expiring.data.items || []).map((lot) => ({
      id: `task-lot-${lot.lot_id}`, kind: "action", severity: lot.expired ? "error" : "warning",
      title: `Lotto ${lot.expired ? "scaduto" : "in scadenza"}: ${lot.ingredient}`,
      message: `${lot.supplier_lot} · ${lot.supplier}${lot.expired ? "" : ` · ${lot.days_left} giorni rimanenti`}`,
      link: `/app/lotti?lot_id=${encodeURIComponent(lot.lot_id)}`,
    }));
    setActionItems([...(actions.data.items || []), ...lotItems]);
  }).catch(() => {});
  useEffect(() => {
    let mounted = true;
    const refresh = () => {
      if (!mounted) return;
      const currentDay = romeDateKey();
      if (currentDay !== activeDay.current) {
        activeDay.current = currentDay;
        setDismissed(new Set());
      }
      fetchN();
    };
    refresh();
    const t = setInterval(refresh, 60000);
    const onVisibility = () => { if (!document.hidden) refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      mounted = false;
      clearInterval(t);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  const notificationItems = items.map((item) => ({ ...item, kind: "notification" }));
  const allItems = [...actionItems, ...notificationItems];
  const isRead = (item) => item.kind === "notification" ? item.read : dismissed.has(item.id);
  const unread = allItems.filter((item) => !isRead(item)).length;
  const count = unread;
  const displayCount = count > 99 ? "99+" : count;
  useEffect(() => {
    if (!("setAppBadge" in navigator)) return;
    try {
      if (count > 0) navigator.setAppBadge(count).catch(() => {});
      else navigator.clearAppBadge().catch(() => {});
    } catch { /* non supportato su questo dispositivo/browser */ }
  }, [count]);
  const markRead = async (item, navigate = true) => {
    if (item.kind === "notification") {
      if (!item.read) await api.patch(`/app/notifications/${item.id}/read`);
      setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, read: true } : entry));
    } else {
      setDismissed((current) => new Set([...current, item.id]));
    }
    if (navigate && item.link) { setOpen(false); nav(item.link); }
  };
  const markAll = async () => {
    await api.post("/app/notifications/read-all");
    setItems((current) => current.map((item) => ({ ...item, read: true })));
    setDismissed((current) => new Set([...current, ...actionItems.map((item) => item.id)]));
  };
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`relative min-h-[44px] rounded-lg p-2.5 text-navy hover:bg-slate-100 ${count > 0 ? "animate-pulse bg-red-50 text-red-700 ring-2 ring-red-200" : ""}`}
        aria-label={`Notifiche: ${count}`}
        aria-expanded={open}
        data-testid="header-notifiche"
      >
        <Bell className="w-5 h-5" strokeWidth={1.6} />
        {count > 0 && <span
          className="absolute right-0 top-0 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-white bg-red-600 px-1 font-body text-[9px] font-bold leading-none text-white shadow-sm"
          aria-hidden="true"
          data-testid="notif-badge"
        >
          {displayCount}
        </span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed inset-x-3 top-[calc(4.5rem+env(safe-area-inset-top))] z-50 max-h-[calc(100dvh-6rem-env(safe-area-inset-bottom))] overflow-hidden rounded-xl bg-white shadow-card ring-1 ring-hairline sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[380px] sm:max-w-[92vw]" data-testid="notif-dropdown">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-hairline">
              <div><div className="font-heading font-semibold text-navy text-sm">Notifiche e adempimenti</div><div className="text-xs text-slate-400 font-body">{unread ? `${unread} da leggere` : "Tutto verificato per questa sessione"}</div></div>
              {unread > 0 && <button type="button" onClick={markAll} className="inline-flex min-h-[36px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-blue-700 hover:bg-blue-50" data-testid="notif-read-all"><CheckCheck className="h-4 w-4" /> Spunta tutte</button>}
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-hairline">
              {allItems.map((item) => { const read = isRead(item);
                const severityStyle = read ? { border: "border-l-transparent bg-white opacity-60", title: "text-navy", message: "text-slate-500" }
                  : item.severity === "confirmation" ? { border: "border-l-violet-500 bg-violet-50/70", title: "text-violet-900", message: "font-medium text-violet-700" }
                  : item.severity === "error" ? { border: "border-l-red-500 bg-red-50/70", title: "text-red-800", message: "font-medium text-red-700" }
                  : { border: "border-l-amber-500 bg-amber-50/60", title: "text-navy", message: "text-slate-500" };
                return <div key={`${item.kind}-${item.id}`} className={`flex items-start gap-3 border-l-4 px-4 py-3 ${severityStyle.border}`} data-testid={`notif-item-${item.id}`}><input type="checkbox" checked={read} onChange={() => !read && markRead(item, false)} className="mt-1 h-5 w-5 shrink-0 rounded accent-emerald-600" aria-label={`Spunta ${item.title}`} /><button type="button" onClick={() => { if (!read) markRead(item, true); else if (item.link) { setOpen(false); nav(item.link); } }} className="min-w-0 flex-1 text-left"><div className="flex items-center gap-2"><span className={`text-sm font-semibold ${severityStyle.title}`}>{item.title}</span>{item.kind === "action" && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-700">Da risolvere</span>}{item.severity === "confirmation" && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-violet-700">Da confermare</span>}</div><p className={`mt-1 text-xs leading-relaxed ${severityStyle.message}`}>{item.message}</p>{item.created_at && <div className="mt-1 text-[10px] text-slate-400">{formatItalianDateTime(item.created_at)}</div>}{item.kind === "action" && read && <div className="mt-1 text-[10px] font-medium text-blue-600">Ricomparirà al prossimo accesso se non risolto.</div>}</button></div>; })}
              {allItems.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">Nessuna notifica presente.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Ricette in bozza: finché il titolare non le approva tutte il menu resta bloccato
// (lucchetti rossi). Lampeggia per non confonderlo con un modulo non attivo.
function DraftRecipesBanner({ count, onOpen }) {
  return (
    <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-3" role="alert" data-testid="draft-recipes-banner">
      <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex flex-1 items-center gap-3 animate-pulse motion-reduce:animate-none">
          <Lock className="w-5 h-5 text-haccp-error shrink-0" />
          <div className="text-sm text-red-800 font-body">
            <strong className="font-heading">{count === 1 ? "C’è 1 ricetta da approvare." : `Ci sono ${count} ricette da approvare.`}</strong>{" "}
            Finché non approvi tutte le ricette in bozza, le voci del menu restano bloccate con il lucchetto.
          </div>
        </div>
        <button type="button" onClick={onOpen} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-navy text-white text-sm min-h-[44px]" data-testid="draft-recipes-open">
          <ChefHat className="w-4 h-4" /> Vai alle ricette
        </button>
      </div>
    </div>
  );
}

function SuspensionBanner({ tenant }) {
  return (
    <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-3" data-testid="suspension-banner">
      <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-haccp-error shrink-0" />
        <div className="flex-1 text-sm text-red-800 font-body">
          <strong className="font-heading">Contratto di consulenza non attivo.</strong>{" "}
          {tenant.status === "limited"
            ? "Per 3 giorni restano disponibili consultazione ed esportazione. Le nuove registrazioni sono sospese."
            : "Accesso riservato ai clienti con contratto di consulenza attivo. I dati e le registrazioni restano conservati. Contatta DF Consulting Group per la riattivazione."}
        </div>
        <div className="flex gap-2">
          <a href={`tel:${BRAND.phone}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy text-white text-sm min-h-[44px]"><Phone className="w-4 h-4" /> Chiama</a>
          <a href={`https://wa.me/${BRAND.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#25D366] text-white text-sm min-h-[44px]"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        </div>
      </div>
    </div>
  );
}

// Avviso ⚡ del consulente per registrazioni mancanti (backend/compliance_alerts.py):
// resta in cima a ogni pagina finché titolare o responsabile non ne prendono visione.
// Durante "Entra come" il consulente lo vede ma non può confermarlo al posto del cliente.
function ComplianceAlertBanner({ impersonated, onOpen }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const fetchAlerts = () => api.get("/app/compliance-alerts").then(({ data }) => setItems(data.items || [])).catch(() => {});
    fetchAlerts();
    const t = setInterval(fetchAlerts, 5 * 60000);
    window.addEventListener("focus", fetchAlerts);
    return () => { clearInterval(t); window.removeEventListener("focus", fetchAlerts); };
  }, []);
  if (!items.length) return null;
  const alert = items[0];
  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.post(`/app/compliance-alerts/${alert.id}/acknowledge`);
      setItems((cur) => cur.filter((a) => a.id !== alert.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita, riprova");
    } finally { setBusy(false); }
  };
  return (
    <div className="border-b-2 border-red-300 bg-red-50 px-4 py-3 sm:px-6" role="alert" data-testid="compliance-alert-banner">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-3 sm:flex-row sm:items-center">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-600 text-white"><Zap className="h-5 w-5" fill="currentColor" /></div>
        <div className="flex-1 font-body text-sm text-red-900">
          <strong className="font-heading">Avviso del tuo consulente HACCP{items.length > 1 ? ` (${items.length})` : ""}.</strong>{" "}
          {alert.intro} {alert.message}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onOpen} className="min-h-[44px] rounded-lg bg-navy px-3 text-sm font-semibold text-white">Vai ai controlli</button>
          {!impersonated && <button type="button" disabled={busy} onClick={acknowledge} className="min-h-[44px] rounded-lg border border-red-300 bg-white px-3 text-sm font-semibold text-red-800 hover:bg-red-100 disabled:opacity-60" data-testid="compliance-alert-ack">{busy ? "…" : "Ho preso visione"}</button>}
        </div>
      </div>
    </div>
  );
}

// "Entra come [utente]" del consulente (TenantDetail.jsx): questo banner
// resta visibile su ogni pagina finché dura la sessione impersonata, cosi'
// e' sempre chiaro di non essere loggati come il cliente vero.
function ImpersonationBanner({ tenantName }) {
  const [returning, setReturning] = useState(false);
  const returnToAdmin = async () => {
    setReturning(true);
    try {
      await api.post("/auth/return-to-admin");
      window.location.href = "/admin";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossibile tornare al pannello admin: rifai il login.");
      setReturning(false);
    }
  };
  return (
    <div className="bg-amber-50 border-b border-amber-300 px-4 sm:px-6 py-3" data-testid="impersonation-banner">
      <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0" />
        <div className="flex-1 text-sm text-amber-900 font-body">
          <strong className="font-heading">Stai visualizzando come cliente{tenantName ? ` — ${tenantName}` : ""}.</strong>{" "}
          Sei entrato come consulente, senza usare le credenziali del cliente.
        </div>
        <button onClick={returnToAdmin} disabled={returning} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy text-white text-sm min-h-[44px] disabled:opacity-60" data-testid="return-to-admin">
          {returning ? "Ritorno…" : "Torna al pannello admin"}
        </button>
      </div>
    </div>
  );
}
